import { Bot } from "lucide-react";
import inferbotAvatar from "@/assets/inferbot.png";
import { NounSvg } from "@/components/noun-svg";
import { useNounSvg } from "@/hooks/use-noun-svg";
import { avatarColor } from "@/lib/avatar";
import { type AvatarPreset, defaultAvatarPreset, findPreset } from "@/lib/avatar-presets";
import { cn } from "@/lib/utils";

type Size = "sm" | "md" | "lg";

const SIZE_CLASS: Record<Size, string> = {
  sm: "h-4 w-4",
  md: "h-[22px] w-[22px]",
  lg: "h-9 w-9",
};

const ICON_CLASS: Record<Size, string> = {
  sm: "h-3 w-3",
  md: "h-3.5 w-3.5",
  lg: "h-4 w-4",
};

const FONT_CLASS: Record<Size, string> = {
  sm: "text-[8px]",
  md: "text-[10px]",
  lg: "text-sm",
};

export function OwnerAvatar({
  name,
  isService,
  avatarUrl,
  size = "md",
}: {
  name: string;
  isService?: boolean;
  avatarUrl?: string | null;
  size?: Size;
}) {
  const sizeClass = SIZE_CLASS[size];

  if (isService) {
    if (name.toLowerCase() === "inferbot") {
      return <img src={inferbotAvatar} alt="inferbot" className={cn("rounded-full", sizeClass)} />;
    }
    return (
      <div
        className={cn("flex items-center justify-center rounded-full bg-gray-500/20", sizeClass)}
      >
        <Bot className={cn(ICON_CLASS[size], "text-gray-500")} />
      </div>
    );
  }

  const preset = findPreset(avatarUrl) ?? defaultAvatarPreset(name);
  return <NounAvatar name={name} preset={preset} size={size} />;
}

function NounAvatar({ name, preset, size }: { name: string; preset: AvatarPreset; size: Size }) {
  const { data: svg, isError } = useNounSvg(preset.nounId);
  const sizeClass = SIZE_CLASS[size];

  if (isError) {
    return (
      <div
        className={cn(
          "flex items-center justify-center rounded-full font-semibold text-white",
          FONT_CLASS[size],
          sizeClass,
          avatarColor(name),
        )}
      >
        {name.charAt(0).toUpperCase()}
      </div>
    );
  }

  return (
    <div
      role="img"
      aria-label={`${name} avatar`}
      className={cn(
        "flex items-center justify-center overflow-hidden rounded-full",
        sizeClass,
        preset.bgClass,
      )}
    >
      {svg && <NounSvg svg={svg} />}
    </div>
  );
}
